import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, Lock, Save, Send, ShieldCheck, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, humanize, pollJob } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { DemoBadge, ErrorState, Loading, Panel, StatusBadge } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import StrategyEditor from "@/components/strategy/StrategyEditor";
import CampaignExport from "@/components/CampaignExport";

const LOCKED = ["strategy_approved", "cancelled", "active", "completed"];

function Generating({ onRetry, failed }) {
  return (
    <div data-testid="strategy-generating" className="cq-card p-10 text-center relative overflow-hidden">
      {!failed && <div className="absolute top-0 left-0 h-px w-1/3 cq-scan" style={{ background: "var(--cq-gradient)" }} />}
      {failed ? <XCircle className="mx-auto text-[#FF7792]" /> : <Sparkles className="mx-auto text-[#22E6F2] animate-pulse" />}
      <div className="mt-4 font-display text-lg text-[#F7FAFF]">{failed ? "Strategy generation failed" : "Drafting your campaign strategy"}</div>
      <p className="mt-1 text-sm text-[#AAB6CC]">{failed ? "The AI engine could not complete the draft." : "Positioning, channel mix, phases and measurement plan. This usually takes under a minute."}</p>
      {onRetry && <button data-testid="generate-strategy-button" onClick={onRetry} className="cq-btn cq-btn-primary mt-6"><Sparkles size={14} /> {failed ? "Try again" : "Generate strategy"}</button>}
    </div>
  );
}

function PlanPanel({ c, onReload }) {
  const { path, can } = useWorkspace();
  const [gen, setGen] = useState("");
  if (!c.phases?.length) return null;
  const undrafted = c.sequence.filter((s) => !s.content_id).length;
  const generate = async (ids = []) => {
    setGen(ids[0] || "all");
    try {
      const { data } = await api.post(path(`/campaigns/${c.id}/generate-content`), { sequence_item_ids: ids });
      toast.info(`Drafting ${data.count} piece${data.count > 1 ? "s" : ""} from your Brand Brain and approved assets…`);
      const r = await pollJob(path(`/jobs/${data.job_id}`));
      toast.success(`${r.count} draft${r.count === 1 ? "" : "s"} ready in Content. Nothing has been published.`);
      onReload();
    } catch (e) { toast.error(e.response ? apiError(e) : e.message); } finally { setGen(""); }
  };
  return (
    <Panel title="Approved plan" eyebrow="Phases & sequence" testId="approved-plan-panel"
      action={c.status === "strategy_approved" && can("edit") && undrafted > 0 && (
        <button data-testid="generate-content-drafts-button" disabled={!!gen} onClick={() => generate()} className="cq-btn cq-btn-primary !py-1.5 !text-xs">
          {gen === "all" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft content for {Math.min(undrafted, 8)} activities
        </button>)}>
      <div className="grid md:grid-cols-3 gap-3">
        {c.phases.map((p) => (
          <div key={p.id} className="p-4 rounded-lg bg-[#050816]/60 border border-[#172342]">
            <div className="font-display text-sm text-[#F7FAFF]">{p.name} <span className="text-xs text-[#7C8BAD]">· {p.timing}</span></div>
            <ul className="mt-2 space-y-2">{c.sequence.filter((s) => s.phase_id === p.id).map((s) => (
              <li key={s.id} data-testid={`sequence-item-${s.id}`} className="text-xs text-[#D5DDEB] flex items-center justify-between gap-2">
                <span className="min-w-0">{s.title}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {s.content_id ? <Link to={`/content?id=${s.content_id}`} data-testid={`sequence-open-draft-${s.id}`} className="text-[#22E6F2] hover:underline">Open draft</Link>
                    : c.status === "strategy_approved" && can("edit") && <button data-testid={`sequence-draft-${s.id}`} disabled={!!gen} onClick={() => generate([s.id])} className="text-[#22E6F2] hover:underline disabled:opacity-50">{gen === s.id ? "Drafting…" : "Draft"}</button>}
                  <StatusBadge status={s.status} />
                </span>
              </li>))}
            </ul>
          </div>))}
      </div>
      <p className="mt-4 text-xs text-[#7C8BAD] flex items-center gap-2"><Lock size={12} /> Drafts use your Brand Brain and approved assets and stay in Draft until approved. Publishing is not available in this build.</p>
    </Panel>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { path, wid } = useWorkspace();
  const q = useWsQuery(`/campaigns/${id}`);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [genState, setGenState] = useState("idle");
  const [confirm, setConfirm] = useState(null);
  const c = q.data;

  useEffect(() => { if (c?.strategy && !dirty) setDraft(c.strategy); }, [c, dirty]);
  const reload = () => { qc.invalidateQueries({ queryKey: ["ws", wid] }); };

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!c || c.strategy || !c.strategy_job_id || genState !== "idle") return;
    setGenState("running");
    pollJob(path(`/jobs/${c.strategy_job_id}`)).then(() => { setGenState("idle"); reload(); toast.success("Strategy draft ready for review"); })
      .catch(() => setGenState("failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.strategy_job_id, c?.strategy]);

  if (q.isLoading) return <Loading label="Loading campaign" />;
  if (q.error) return <ErrorState message={apiError(q.error)} onRetry={q.refetch} />;

  const locked = LOCKED.includes(c.status);
  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); setDirty(false); reload(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const save = () => act("save", () => api.put(path(`/campaigns/${id}/strategy`), { strategy: draft }), "Draft saved");
  const generate = async () => {
    try { await api.post(path(`/campaigns/${id}/generate-strategy`)); setGenState("idle"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const approve = () => act("approve", async () => {
    if (dirty) await api.put(path(`/campaigns/${id}/strategy`), { strategy: draft });
    await api.post(path(`/campaigns/${id}/approve-strategy`), { confirm: true });
  }, "Strategy approved and logged. Nothing has been launched.");
  const submit = () => act("submit", async () => {
    if (dirty) await api.put(path(`/campaigns/${id}/strategy`), { strategy: draft });
    await api.post(path(`/campaigns/${id}/submit-for-approval`));
  }, "Submitted to the Approval Queue");
  const cancel = () => act("cancel", () => api.post(path(`/campaigns/${id}/cancel`), { confirm: true }), "Campaign cancelled");

  return (
    <div className="space-y-6">
      <Link to="/campaigns" data-testid="back-to-campaigns" onClick={(e) => { if (dirty && !window.confirm("You have unsaved strategy changes. Leave without saving?")) e.preventDefault(); }} className="inline-flex items-center gap-1.5 text-sm text-[#AAB6CC] hover:text-[#22E6F2]"><ArrowLeft size={14} /> Campaigns</Link>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 cq-rise">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 mb-3"><span className="cq-label">Campaign strategy review</span><StatusBadge status={c.status} testId="campaign-status" />{c.is_demo && <DemoBadge />}</div>
          <h1 data-testid="campaign-title" className="text-3xl sm:text-4xl font-semibold text-[#F7FAFF]">{draft?.campaign_name || c.name}</h1>
          <p className="mt-3 text-sm text-[#AAB6CC] font-mono">› {c.command}</p>
        </div>
      </div>

      {c.status === "strategy_approved" && (
        <div data-testid="strategy-approved-banner" className="flex gap-3 p-4 rounded-xl border border-[#4BE3A4]/30 bg-[#4BE3A4]/5 text-sm text-[#F7FAFF]">
          <ShieldCheck size={16} className="text-[#4BE3A4] shrink-0 mt-0.5" /> Strategy approved {c.approved_at ? `on ${fmtDateTime(c.approved_at)}` : ""}. The plan is ready; no content has been published and no budget has moved.
        </div>
      )}
      <PlanPanel c={c} onReload={reload} />
      <CampaignExport c={c} />

      {!c.strategy ? (
        <Generating failed={genState === "failed"} onRetry={genState === "running" || (c.strategy_job_id && genState === "idle") ? null : generate} />
      ) : (
        <StrategyEditor strategy={draft} locked={locked} campaignId={id} onChange={(s) => { setDraft(s); setDirty(true); }} />
      )}

      {c.approvals?.length > 0 && (
        <Panel title="Approval history" testId="campaign-approval-history">
          <div className="divide-y divide-[#172342]">{c.approvals.map((a) => (
            <Link key={a.id} to={`/approvals?id=${a.id}`} className="py-3 flex items-center justify-between gap-3 text-sm hover:text-[#22E6F2]">
              <span className="text-[#F7FAFF]">{a.requested_action}</span>
              <span className="flex items-center gap-3"><span className="text-xs text-[#7C8BAD]">{a.reviewer_name || "Unassigned"}</span><StatusBadge status={a.status} /></span>
            </Link>))}
          </div>
        </Panel>
      )}

      {c.strategy && !locked && (
        <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-8 lg:-mx-10 cq-glass border-t border-[#243153]" data-testid="strategy-action-bar">
          <div className="px-4 sm:px-8 lg:px-10 py-3 flex flex-wrap items-center gap-2 justify-end">
            <span className="mr-auto text-xs text-[#7C8BAD] hidden md:block">{dirty ? "Unsaved changes" : `Status: ${humanize(c.status)}`}</span>
            <button data-testid="strategy-cancel-button" onClick={() => setConfirm("cancel")} className="cq-btn cq-btn-danger">Cancel campaign</button>
            <button data-testid="strategy-save-draft-button" disabled={!dirty || !!busy} onClick={save} className="cq-btn cq-btn-ghost">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft</button>
            {c.status !== "pending_approval" && <button data-testid="strategy-submit-button" disabled={!!busy} onClick={submit} className="cq-btn cq-btn-ghost"><Send size={14} /> Submit for approval</button>}
            <button data-testid="strategy-approve-button" disabled={!!busy} onClick={() => setConfirm("approve")} className="cq-btn cq-btn-primary"><CheckCircle2 size={14} /> Approve strategy</button>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirm === "approve"} onOpenChange={(o) => !o && setConfirm(null)} title="Approve this strategy?"
        description="Approving records your sign-off and converts the plan into phases and sequence items. It does not publish content, launch the campaign or move any money. This decision is logged."
        confirmLabel="Approve strategy" onConfirm={approve} />
      <ConfirmDialog open={confirm === "cancel"} onOpenChange={(o) => !o && setConfirm(null)} title="Cancel this campaign?" danger
        description="The campaign will be marked as cancelled and its strategy locked. This is logged in the activity history." confirmLabel="Cancel campaign"
        onConfirm={() => { cancel(); nav("/campaigns"); }} />
    </div>
  );
}
